import { Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';
import { faker } from '@faker-js/faker';
import { HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import {
  CreateSystemAdminDto,
  SystemAdminDetail,
  SystemAdminStatus,
  UpdateSystemAdminDto,
  UpdateSystemAdminStatusDto,
} from '@app/core/models/admin-system-admin.interface';

interface StoredAdmin extends SystemAdminDetail {
  deletedAt?: string;
}

@Injectable({ providedIn: 'root' })
export class AdminSystemAdminMocksService {
  private storageKey = 'mock_system_admins';

  private loadAdmins(): StoredAdmin[] {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) return JSON.parse(stored);
    } catch {
      // ignore
    }
    return [];
  }

  private saveAdmins(admins: StoredAdmin[]): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(admins));
    } catch {
      // ignore
    }
  }

  private ensureAdmins(): StoredAdmin[] {
    let admins = this.loadAdmins();
    if (admins.length === 0) {
      admins = this.generateMockAdmins();
      this.saveAdmins(admins);
    }
    return admins;
  }

  private generateMockAdmins(): StoredAdmin[] {
    const statuses: SystemAdminStatus[] = ['ACTIVE', 'INACTIVE', 'FORCE_CHANGE_PASSWORD'];

    return Array.from({ length: 5 }, (_, i) => {
      const createdAt = faker.date.past({ years: 2 }).toISOString();
      return {
        userId: faker.string.uuid(),
        email: faker.internet.email(),
        name: faker.person.fullName(),
        status: i < 2 ? 'ACTIVE' : faker.helpers.arrayElement(statuses),
        createdAt,
        updatedAt: faker.date.recent({ days: 30 }).toISOString(),
      } as StoredAdmin;
    });
  }

  handleMockRequest(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    const urlParts = req.url.split('/');
    const systemAdminsIndex = urlParts.indexOf('system-admins');
    const afterSystemAdmins = urlParts.slice(systemAdminsIndex + 1);
    const userId = afterSystemAdmins[0];
    const isStatusEndpoint = afterSystemAdmins[1] === 'status';

    if (req.method === 'GET' && !userId) {
      return this.listAdmins(req);
    } else if (req.method === 'POST' && !userId) {
      return this.createAdmin(req.body as CreateSystemAdminDto);
    } else if (req.method === 'PUT' && userId && !isStatusEndpoint) {
      return this.updateAdmin(userId, req.body as UpdateSystemAdminDto);
    } else if (req.method === 'PATCH' && userId && isStatusEndpoint) {
      return this.updateAdminStatus(userId, req.body as UpdateSystemAdminStatusDto);
    } else if (req.method === 'DELETE' && userId) {
      return this.deleteAdmin(userId);
    }

    throw new Error(`Unsupported mock system-admin request: ${req.method} ${req.url}`);
  }

  private listAdmins(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    const url = new URL(req.url);
    const page = parseInt(url.searchParams.get('page') || '1', 10);
    const limit = parseInt(url.searchParams.get('limit') || '10', 10);
    const q = url.searchParams.get('q') || '';
    const status = url.searchParams.get('status') as SystemAdminStatus | null;

    const allAdmins = this.ensureAdmins();
    const filtered = allAdmins.filter((a) => {
      if (a.deletedAt) return false;
      const matchesQuery =
        !q ||
        (a.email && a.email.toLowerCase().includes(q.toLowerCase())) ||
        (a.name && a.name.toLowerCase().includes(q.toLowerCase()));
      const matchesStatus = !status || a.status === status;
      return matchesQuery && matchesStatus;
    });

    const total = filtered.length;
    const data = filtered.slice((page - 1) * limit, page * limit);

    return this.respond({ data, total, page, limit, totalPages: Math.ceil(total / limit) }, 200);
  }

  private createAdmin(dto: CreateSystemAdminDto): Observable<HttpEvent<any>> {
    const admins = this.ensureAdmins();
    const newAdmin: StoredAdmin = {
      userId: faker.string.uuid(),
      email: dto.email,
      name: dto.name ?? null,
      status: 'FORCE_CHANGE_PASSWORD',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    admins.push(newAdmin);
    this.saveAdmins(admins);
    return this.respond(newAdmin, 201);
  }

  private updateAdmin(userId: string, dto: UpdateSystemAdminDto): Observable<HttpEvent<any>> {
    const admins = this.ensureAdmins();
    const index = admins.findIndex((a) => a.userId === userId && !a.deletedAt);
    if (index === -1) return this.respond({ message: 'Admin not found' }, 404);

    admins[index] = {
      ...admins[index],
      ...(dto.name !== undefined ? { name: dto.name } : {}),
      updatedAt: new Date().toISOString(),
    };
    this.saveAdmins(admins);
    return this.respond(admins[index], 200);
  }

  private updateAdminStatus(
    userId: string,
    dto: UpdateSystemAdminStatusDto
  ): Observable<HttpEvent<any>> {
    const admins = this.ensureAdmins();
    const index = admins.findIndex((a) => a.userId === userId && !a.deletedAt);
    if (index === -1) return this.respond({ message: 'Admin not found' }, 404);

    // Check if deactivating the last active system admin
    if (dto.status === 'INACTIVE') {
      const activeAdmins = admins.filter((a) => !a.deletedAt && a.status === 'ACTIVE');
      if (activeAdmins.length <= 1) {
        return this.respond(
          {
            message: 'Cannot deactivate the last active system admin',
            code: 'LAST_ACTIVE_SYSTEM_ADMIN',
          },
          422
        );
      }
    }

    admins[index] = {
      ...admins[index],
      status: dto.status,
      updatedAt: new Date().toISOString(),
    };
    this.saveAdmins(admins);
    return this.respond(admins[index], 200);
  }

  private deleteAdmin(userId: string): Observable<HttpEvent<any>> {
    const admins = this.ensureAdmins();
    const index = admins.findIndex((a) => a.userId === userId && !a.deletedAt);
    if (index === -1) return this.respond({ message: 'Admin not found' }, 404);

    admins[index] = { ...admins[index], deletedAt: new Date().toISOString() };
    this.saveAdmins(admins);
    return this.respond(null, 204);
  }

  private respond<T>(body: T, status: number): Observable<HttpEvent<T>> {
    return of(new HttpResponse({ status, body })).pipe(delay(400));
  }
}
